import React, { memo, useState, useMemo, useContext, useCallback, useEffect, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useLocation, useNavigate } from 'react-router-dom';
import { Container, Button, Card, OverlayTrigger, Tooltip, Table, Form } from 'react-bootstrap';
import { ChevronDown, Gear, InfoCircle, SortDown, SortUp, BookmarkFill, Bookmark } from 'react-bootstrap-icons';
import { AuthContext } from '../contexts/AuthContext';
import { TradingContext } from '../contexts/TradingContext';
import { AppStateContext } from '../contexts/AppStateContext';
import { StreamsContext } from '../contexts/StreamsContext';
import { useGetTicker } from '../hooks/useTradeStation';
import { useBarsFetcher } from '../hooks/useTradeStationQueries';
import usePositionCalculations from '../hooks/usePositionCalculations';
import { useRealtimeAccountMetrics } from '../hooks/useRealtimeAccountMetrics';
import {
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  Cell,
  LabelList,
} from 'recharts';
import { useTheme } from '../contexts/ThemeContext';
import { Header } from '../components/Header';
import useIsMobile, { useMaxWidth } from '../hooks/useIsMobile';
import {
  usePerformanceData,
  usePerformanceRecentTrades,
  RECENT_TRADES_PAGE_SIZE,
} from '../hooks/usePerformanceData';
import PerformanceSyncModal from '../components/Performance/PerformanceSyncModal';
import PerformanceTradeSyncModal from '../components/Performance/PerformanceTradeSyncModal';
import useTradeHistorySyncJobMonitor from '../hooks/useTradeHistorySyncJobMonitor';
import PerformanceTimeframeBar, {
  getTimeframeScopeLabel,
  TIMEFRAME_PRESETS,
} from '../components/Performance/PerformanceTimeframeBar';
import { readStoredPeriod, writeStoredPeriod } from '../utils/reviewPeriodStorage';
import PerformanceDashboardCalendar, {
  getMonthGridFetchRange,
} from '../components/Performance/PerformanceDashboardCalendar';
import PerformanceCalendarDayModal from '../components/Performance/PerformanceCalendarDayModal';
import EquityCurvePlot from '../components/Performance/EquityCurvePlot';
import AccountTransferModal from '../components/Performance/AccountTransferModal';
import { useTradeJournals } from '../hooks/useTradeJournals';
import { useAccountTransfers } from '../hooks/useAccountTransfers';
import { BannerContext } from '../contexts/BannerContext';
import ThemedSelect from '../components/shared/ThemedSelect';
import AccountDropdown from '../components/shared/AccountDropdown';
import BrokerNotConnectedState from '../components/shared/BrokerNotConnectedState';
import { isBrokerLinked, canAccessMarketDataTools, canAccessTradeWorkspace } from '../utils/brokerConnection';
import {
  closesMapFromBars,
  mergeBenchmarkPctIntoCurve,
  mergeDrawdownIntoCurve,
  pctFromBaseline as pctFromEquityBaselineUtil,
  shiftDateString,
} from '../utils/benchmarkEquitySeries';
import {
  applyTransferAdjustedPnl,
  getTransferAdjustedSnapshotPnl,
} from '../utils/transferAdjustedEquity';
import { applyLiveBalanceToEquityCurve } from '../utils/liveEquityCurve';
import PrimaryButton from '../components/shared/PrimaryButton';
import OutlinePrimaryButton from '../components/shared/OutlinePrimaryButton';
import DashboardPageHeader from '../components/shared/DashboardPageHeader';
import { computeTradePnlPercent } from '../utils/tradePnlPercent';
import { tradeDirectionLabel } from '../utils/tradeDirectionLabel';
import DashboardSectionTitle from '../components/shared/DashboardSectionTitle';
import { getDashboardPageShellStyle } from '../constants/dashboardLayout';
import { MOBILE_UI } from '../constants/mobileUi';

const STAT_TOOLTIPS = {
  netPnl: 'Realized profit or loss from trades we have recorded in the selected timeframe. May not include all account activity.',
  snapshotPnl: 'Account value change for the selected period, based on daily account snapshots. Includes open positions; deposits and withdrawals are removed.',
  winRate: 'Percentage of trades that were profitable.',
  profitFactor: 'Gross profit divided by gross loss.',
  expectancy: 'Average expected profit per trade.',
  sharpeRatio: 'Risk-adjusted return from each trade’s P&L in the selected timeframe (includes open positions when that option is on).',
  sortinoRatio: 'Like Sharpe, using only losing P&L for downside deviation (includes open positions when that option is on).',
  totalTrades: 'Total number of completed trades in the timeframe.',
};

const formatCurrency = (v) => {
  const abs = Math.abs(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return v >= 0 ? `+$${abs}` : `-$${abs}`;
};
const formatPercent = (v) => `${(v * 100).toFixed(1)}%`;
/** Account balances are levels, not changes — a leading sign would misread as performance. */
const formatCurrencyLevel = (v) =>
  `$${Number(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/**
 * Marks which slice of history a section covers. The page timeframe control scrolls out of
 * view long before the sections it governs, so each scoped section restates its own range.
 */
const ScopeChip = ({ label, colors }) =>
  label ? (
    <span
      style={{
        fontSize: '0.6875rem',
        fontWeight: 600,
        letterSpacing: '0.04em',
        textTransform: 'uppercase',
        color: colors.textMuted,
        backgroundColor: colors.bgSecondary,
        border: `1px solid ${colors.border}`,
        borderRadius: 999,
        padding: '2px 8px',
        whiteSpace: 'nowrap',
      }}
    >
      {label}
    </span>
  ) : null;

/** Hero: Current Equity vs All-Time High */
const EquityVsAthHero = ({ summary, todayPnl, colors, isMobile, cardStyle }) => {
  const hasSnapshotHistory = summary.allTimeHigh != null && summary.allTimeHigh > 0;
  const isNewHigh = hasSnapshotHistory && summary.currentEquity != null && summary.currentEquity >= summary.allTimeHigh;
  const distance = (summary.currentEquity != null && summary.allTimeHigh != null)
    ? summary.currentEquity - summary.allTimeHigh
    : null;
  const tiles = [
    {
      label: 'Current Equity',
      value: summary.currentEquity != null ? `$${Number(summary.currentEquity).toLocaleString()}` : '—',
      valueColor: colors.text,
    },
    {
      label: 'All-Time High',
      value: summary.allTimeHigh != null ? `$${Number(summary.allTimeHigh).toLocaleString()}` : '—',
      valueColor: colors.text,
    },
  ];

  return (
    <Card
      style={{
        backgroundColor: colors.bgCard,
        border: `1px solid ${colors.border}`,
        borderRadius: 8,
        marginBottom: '24px',
        ...cardStyle,
      }}
    >
      <Card.Body style={{ padding: '12px 16px' }}>
        <div style={{ fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: colors.text, marginBottom: '12px' }}>Account Snapshot</div>
        {isNewHigh && (
          <div
            style={{
              textAlign: 'center',
              paddingBottom: '8px',
              fontSize: '0.65rem',
              fontWeight: 700,
              letterSpacing: '0.14em',
              color: colors.chartUp,
            }}
          >
            NEW ALL-TIME HIGH
          </div>
        )}
        {tiles.map((tile, i) => (
          <div
            key={tile.label}
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '6px 0',
              borderBottom: undefined,
            }}
          >
            <span style={{ fontSize: '0.8rem', color: colors.textMuted }}>
              {tile.label === 'Drawdown' ? (
                <OverlayTrigger placement="top" overlay={<Tooltip>Distance from your all-time account high. Measures peak-to-current equity decline.</Tooltip>}>
                  <span style={{ cursor: 'help', borderBottom: `1px dashed ${colors.textMuted}` }}>{tile.label}</span>
                </OverlayTrigger>
              ) : tile.label}
            </span>
            <span style={{ fontSize: '0.9rem', fontWeight: 600, color: tile.valueColor }}>{tile.value}</span>
          </div>
        ))}
      </Card.Body>
    </Card>
  );
};

/** Key stats row */
const KeyStatsRow = ({ summary, equity, colors, isMobile = false, transfers = [] }) => {
  const [showAdvanced, setShowAdvanced] = useState(false);
  const computeRiskAdjustedRatios = () => {
    const tradePnls = Array.isArray(summary?.tradePnls)
      ? summary.tradePnls.map((n) => Number(n)).filter((n) => Number.isFinite(n))
      : [];
    if (tradePnls.length < 2) return { sharpeRatio: null, sortinoRatio: null };

    const capital =
      (Number.isFinite(Number(summary?.currentEquity)) && Number(summary.currentEquity) > 0
        ? Number(summary.currentEquity)
        : null) ||
      (Array.isArray(equity?.equityCurve)
        ? Number(equity.equityCurve.find((p) => Number(p?.equity) > 0)?.equity)
        : null);
    if (!(capital > 0)) return { sharpeRatio: null, sortinoRatio: null };

    // One observation per trade P&L (closed + open when include-open is on).
    const returns = tradePnls.map((pnl) => pnl / capital);
    const mean = returns.reduce((sum, r) => sum + r, 0) / returns.length;
    const variance = returns.reduce((sum, r) => sum + Math.pow(r - mean, 2), 0) / returns.length;
    const stdev = Math.sqrt(variance);
    const downsideVariance = returns.reduce(
      (sum, r) => sum + Math.pow(Math.min(r, 0), 2),
      0
    ) / returns.length;
    const downsideDeviation = Math.sqrt(downsideVariance);

    // Annualize by observed trade frequency over the sample span when possible;
    // otherwise fall back to √n (period ratio scaled by sample size).
    let annualizationFactor = Math.sqrt(returns.length);
    const first = summary?.rangeStart || equity?.rangeStart;
    const last = summary?.rangeEnd || equity?.rangeEnd;
    if (first && last) {
      const spanMs = new Date(`${last}T12:00:00`).getTime() - new Date(`${first}T12:00:00`).getTime();
      const spanDays = Math.max(1, spanMs / (24 * 60 * 60 * 1000));
      const tradesPerYear = returns.length * (365.25 / spanDays);
      if (Number.isFinite(tradesPerYear) && tradesPerYear > 0) {
        annualizationFactor = Math.sqrt(tradesPerYear);
      }
    }

    const annualizedSharpe = Number.isFinite(stdev) && stdev > 0
      ? (mean / stdev) * annualizationFactor
      : null;
    const annualizedSortino = Number.isFinite(downsideDeviation) && downsideDeviation > 0
      ? (mean / downsideDeviation) * annualizationFactor
      : null;

    return {
      sharpeRatio: Number.isFinite(annualizedSharpe) ? annualizedSharpe : null,
      sortinoRatio: Number.isFinite(annualizedSortino) ? annualizedSortino : null,
    };
  };

  const { sharpeRatio, sortinoRatio } = computeRiskAdjustedRatios();
  const avgWinColor = summary.avgWin > 0 ? colors.chartUp : colors.text;
  const avgLossColor = summary.avgLoss < 0 ? colors.chartDown : colors.text;
  const bestTradeColor = summary.bestTrade > 0 ? colors.chartUp : colors.text;
  const worstTradeColor = summary.worstTrade < 0 ? colors.chartDown : colors.text;

  const {
    amount: snapshotPnl,
    pct: snapshotPnlPct,
    netTransfers,
  } = getTransferAdjustedSnapshotPnl(equity, transfers);
  const realizedPnl = summary.netPnl;

  const pnlColor = (v) => (v == null ? colors.textMuted : v >= 0 ? colors.chartUp : colors.chartDown);

  const metricLabel = (key, label, adornment = null, { flush = false } = {}) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginBottom: flush ? 0 : 4, flexWrap: 'wrap' }}>
      <span style={{
        fontSize: '0.65rem',
        fontWeight: 600,
        textTransform: 'uppercase',
        letterSpacing: '0.06em',
        color: colors.textMuted,
      }}>
        {label}
      </span>
      {adornment}
      {STAT_TOOLTIPS[key] && (
        <OverlayTrigger
          placement="top"
          rootClose
          // Hover alone never fires on touch, so these hints were unreachable on mobile.
          trigger={['hover', 'focus', 'click']}
          overlay={<Tooltip id={`stat-tip-${key}`}>{STAT_TOOLTIPS[key]}</Tooltip>}
        >
          <span
            role="button"
            tabIndex={0}
            aria-label={`What is ${label}?`}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: colors.textMuted,
              cursor: 'help',
              lineHeight: 1,
              minWidth: 24,
              minHeight: 24,
              margin: -4,
            }}
          >
            <InfoCircle size={14} aria-hidden />
          </span>
        </OverlayTrigger>
      )}
    </div>
  );

  const pnlHero = (key, label, amount, pct, note = null, { showPct = true, labelAdornment = null } = {}) => (
    <div key={key} style={{ minWidth: 0 }}>
      {metricLabel(key, label, labelAdornment)}
      <div style={{
        display: 'flex',
        alignItems: 'baseline',
        gap: 10,
        flexWrap: 'wrap',
      }}>
        <span style={{
          fontSize: isMobile ? '1.5rem' : '1.75rem',
          fontWeight: 750,
          color: pnlColor(amount),
          lineHeight: 1,
          fontVariantNumeric: 'tabular-nums',
          letterSpacing: '-0.025em',
        }}>
          {amount != null ? formatCurrency(amount) : '—'}
        </span>
        {showPct && (
          <span style={{ fontSize: '0.85rem', fontWeight: 650, color: pnlColor(pct) }}>
            {pct != null ? `${pct >= 0 ? '+' : ''}${pct.toFixed(2)}%` : '—'}
          </span>
        )}
      </div>
      {note ? (
        <div style={{ marginTop: 6, fontSize: '0.7rem', color: colors.textMuted }}>{note}</div>
      ) : null}
    </div>
  );

  const compactMetric = (key, label, value, valueColor = colors.text) => (
    <div key={key} style={{ minWidth: 0 }}>
      {metricLabel(key, label)}
      <div style={{
        fontSize: '1rem',
        fontWeight: 700,
        color: valueColor,
        lineHeight: 1.2,
        fontVariantNumeric: 'tabular-nums',
      }}>
        {value}
      </div>
    </div>
  );

  const qualityGrid = {
    display: 'grid',
    gridTemplateColumns: isMobile ? 'repeat(2, minmax(0, 1fr))' : 'repeat(auto-fit, minmax(148px, 1fr))',
    gap: isMobile ? '14px 16px' : '18px 24px',
  };

  return (
    <Card
      style={{
        backgroundColor: colors.bgCard,
        border: `1px solid ${colors.border}`,
        borderRadius: 12,
        marginBottom: isMobile ? 20 : 24,
      }}
    >
      <Card.Body style={{ padding: isMobile ? '16px 14px' : '22px 24px' }}>
        {pnlHero(
          'snapshotPnl',
          'Account P&L',
          snapshotPnl,
          snapshotPnlPct,
          netTransfers
            ? `Excludes ${netTransfers > 0 ? 'net deposits' : 'net withdrawals'} of ${formatCurrency(Math.abs(netTransfers))}`
            : null
        )}

        <div
          style={{
            display: 'flex',
            alignItems: 'baseline',
            justifyContent: 'space-between',
            gap: 12,
            marginTop: 14,
          }}
        >
          {metricLabel('netPnl', 'Realized P&L', null, { flush: true })}
          <div
            style={{
              fontSize: isMobile ? '1.05rem' : '1.125rem',
              fontWeight: 700,
              color: pnlColor(realizedPnl),
              fontVariantNumeric: 'tabular-nums',
              letterSpacing: '-0.02em',
              whiteSpace: 'nowrap',
            }}
          >
            {realizedPnl != null ? formatCurrency(realizedPnl) : '—'}
          </div>
        </div>

        <div
          style={{
            ...qualityGrid,
            marginTop: 16,
            paddingTop: 16,
            borderTop: `1px solid ${colors.border}`,
          }}
        >
          {compactMetric('winRate', 'Win Rate', formatPercent(summary.winRate))}
          {compactMetric('totalTrades', 'Total Trades', summary.totalTrades ?? '—')}
          {compactMetric('profitFactor', 'Profit Factor', summary.profitFactor != null ? summary.profitFactor.toFixed(2) : '—')}
          {compactMetric('expectancy', 'Expectancy', summary.expectancy != null ? formatCurrency(summary.expectancy) : '—')}
          {compactMetric('avgWin', 'Avg Win', formatCurrency(summary.avgWin), avgWinColor)}
          {compactMetric('avgLoss', 'Avg Loss', formatCurrency(summary.avgLoss), avgLossColor)}
        </div>

        <button
          type="button"
          onClick={() => setShowAdvanced((v) => !v)}
          aria-expanded={showAdvanced}
          style={{
            marginTop: 8,
            minHeight: 40,
            padding: '8px 2px',
            background: 'none',
            border: 'none',
            color: colors.textMuted,
            fontSize: '0.8125rem',
            fontWeight: 600,
            cursor: 'pointer',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
          }}
        >
          {showAdvanced ? 'Hide advanced stats' : 'More stats'}
          <ChevronDown
            size={14}
            style={{
              transform: showAdvanced ? 'rotate(180deg)' : 'none',
              transition: 'transform 0.15s ease',
            }}
          />
        </button>
        {showAdvanced && (
          <div style={{ ...qualityGrid, marginTop: 4 }}>
            {compactMetric('sharpeRatio', 'Sharpe Ratio', sharpeRatio != null ? sharpeRatio.toFixed(2) : '—')}
            {compactMetric('sortinoRatio', 'Sortino Ratio', sortinoRatio != null ? sortinoRatio.toFixed(2) : '—')}
            {compactMetric('biggestWin', 'Biggest Win', formatCurrency(summary.bestTrade), bestTradeColor)}
            {compactMetric('biggestLoss', 'Biggest Loss', formatCurrency(summary.worstTrade), worstTradeColor)}
          </div>
        )}
      </Card.Body>
    </Card>
  );
};

const pctFromEquityBaseline = (equity, baseline) => pctFromEquityBaselineUtil(equity, baseline);

const formatRangeDate = (str) => {
  if (!str) return '';
  const d = new Date(`${str}T12:00:00`);
  return d.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
};

const ChartModeToggle = ({ mode, onChange, colors }) => (
  <div
    style={{
      display: 'inline-flex',
      flexShrink: 0,
      borderRadius: '6px',
      border: `1px solid ${colors.border}`,
      overflow: 'hidden',
      fontSize: '0.65rem',
    }}
  >
    {[
      { id: 'value', label: 'Account Value' },
      { id: 'pnl', label: 'P&L%' },
    ].map(({ id, label }) => {
      const active = mode === id;
      return (
        <button
          key={id}
          type="button"
          onClick={() => onChange(id)}
          style={{
            padding: '3px 7px',
            lineHeight: 1.2,
            fontSize: '0.65rem',
            border: 'none',
            cursor: 'pointer',
            background: active ? colors.primary : 'transparent',
            color: active ? '#fff' : colors.textMuted,
            fontWeight: active ? 600 : 400,
          }}
        >
          {label}
        </button>
      );
    })}
  </div>
);

const BenchmarkSelect = ({ value, onChange, colors, isMobile }) => (
  <select
    value={value}
    onChange={(e) => onChange(e.target.value)}
    style={{
      fontSize: isMobile ? '0.7rem' : '0.75rem',
      padding: isMobile ? '4px 8px' : '6px 10px',
      borderRadius: '8px',
      border: `1px solid ${colors.border}`,
      background: colors.bgSecondary,
      color: colors.text,
      cursor: 'pointer',
    }}
    aria-label="Compare to index"
  >
    <option value="SPY">SPY</option>
    <option value="QQQ">QQQ</option>
  </select>
);

/** Equity curve: P&L% (account vs one index) or account value, one scale at a time. */
const EquityCurveChart = ({
  equityData,
  timeframe,
  dateRange = null,
  onTimeframeChange = null,
  colors,
  isMobile,
  externalMobileHeader = false,
  benchmark,
  onBenchmarkChange,
  isLoading = false,
  cardStyle,
  drawdownAmt,
  drawdownPctVal,
  isAtATH,
  currentEquity,
  allTimeHigh,
  transfers = [],
}) => {
  const [chartMode, setChartMode] = useState('value');
  const [scrubPoint, setScrubPoint] = useState(null);
  const [showDrawdown, setShowDrawdown] = useState(true);
  const { user } = useContext(AuthContext);
  const { selectedAccount } = useContext(TradingContext);
  const fetchBars = useBarsFetcher();
  const canOverlayBenchmark = canAccessMarketDataTools(user, selectedAccount);

  const rangeStart = equityData?.rangeStart;
  const rangeEnd = equityData?.rangeEnd;
  const serverHasBenchmark = equityData?.equityCurve?.some((p) => typeof p.benchmarkPct === 'number');
  const needsClientBenchmark =
    chartMode === 'pnl' && !!rangeStart && !!rangeEnd && !serverHasBenchmark && canOverlayBenchmark;

  const benchmarkBarsQuery = useQuery({
    queryKey: ['equity-benchmark-bars', benchmark, rangeStart, rangeEnd, selectedAccount?.AccountID],
    queryFn: async () => {
      const res = await fetchBars({
        ticker: benchmark,
        interval: 1,
        unit: 'Daily',
        sessionTemplate: 'Default',
        firstdate: shiftDateString(rangeStart, -14),
        lastdate: rangeEnd,
      });
      if (res?.Error && !res?.Bars?.length) {
        throw new Error(typeof res.Message === 'string' ? res.Message : 'Benchmark bars unavailable');
      }
      return res;
    },
    enabled: !!user?.token && needsClientBenchmark && !!benchmark,
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });

  const chartData = useMemo(() => {
    const curve = equityData?.equityCurve ?? [];
    if (!curve.length) return [];

    const baselineEquity =
      curve.find((p) => p.equity > 0)?.equity ?? curve[0]?.equity ?? null;

    let rows = curve.map((p) => {
      const pnlPct =
        typeof p.pnlPct === 'number'
          ? p.pnlPct
          : pctFromEquityBaseline(p.equity, baselineEquity);

      let benchmarkPct = p.benchmarkPct;
      if (benchmarkPct == null && typeof p.spyPct === 'number') benchmarkPct = p.spyPct;
      if (benchmarkPct == null && typeof p.qqqPct === 'number') benchmarkPct = p.qqqPct;

      return {
        ...p,
        pnlPct,
        benchmarkPct: typeof benchmarkPct === 'number' ? benchmarkPct : null,
      };
    });

    if (needsClientBenchmark && benchmarkBarsQuery.data?.Bars?.length && rangeStart) {
      const closeByDate = closesMapFromBars(benchmarkBarsQuery.data.Bars);
      rows = mergeBenchmarkPctIntoCurve(rows, closeByDate, rangeStart);
    }

    const { curve: adjustedRows } = applyTransferAdjustedPnl(rows, transfers, {
      rangeStart: rangeStart || equityData?.rangeStart,
      rangeEnd: rangeEnd || equityData?.rangeEnd,
    });

    return mergeDrawdownIntoCurve(adjustedRows, equityData?.drawdownCurve);
  }, [equityData, needsClientBenchmark, benchmarkBarsQuery.data, rangeStart, rangeEnd, transfers]);

  const lastPoint = chartData.length ? chartData[chartData.length - 1] : null;
  const isPnlMode = chartMode === 'pnl';
  const benchmarkSymbol = equityData?.benchmark || benchmark || 'SPY';
  const benchmarkAvailable = chartData.some((p) => typeof p.benchmarkPct === 'number');
  const showBenchmark = canOverlayBenchmark && benchmarkAvailable;
  const benchmarkLoading = needsClientBenchmark && benchmarkBarsQuery.isLoading;
  const drawdownColor = isAtATH ? colors.chartUp : colors.chartDown;

  const baselineAdjustedEquity = useMemo(() => {
    if (!chartData.length) return null;
    return (
      chartData.find((p) => p.adjustedEquity != null && p.adjustedEquity > 0)?.adjustedEquity ??
      chartData.find((p) => p.equity > 0)?.equity ??
      chartData[0]?.equity ??
      null
    );
  }, [chartData]);

  const focusPoint = scrubPoint || lastPoint;
  const headlinePct = isPnlMode ? (focusPoint?.pnlPct ?? equityData?.accountPnlPct) : null;
  const headlinePnlAmount = useMemo(() => {
    if (!isPnlMode) return null;
    const endAdjusted =
      focusPoint?.adjustedEquity != null ? focusPoint.adjustedEquity : focusPoint?.equity;
    if (endAdjusted == null || baselineAdjustedEquity == null) return null;
    return endAdjusted - baselineAdjustedEquity;
  }, [isPnlMode, focusPoint, baselineAdjustedEquity]);
  const scrubDateLabel = scrubPoint?.time
    ? new Date(`${scrubPoint.time}T12:00:00`).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
    : null;

  const showInlineMobileTimeframe =
    isMobile && typeof onTimeframeChange === 'function' && !externalMobileHeader;
  const hideInnerHeadlineLabel = externalMobileHeader && isMobile;
  const showHeaderModeToggle = true;

  const equitySummaryStats = useMemo(() => {
    const stats = [];
    if (currentEquity != null) {
      stats.push({
        label: 'Current Equity',
        value: formatCurrencyLevel(currentEquity),
        color: colors.text,
      });
    }
    if (allTimeHigh != null) {
      stats.push({
        label: 'All-Time High',
        value: formatCurrencyLevel(allTimeHigh),
        color: colors.text,
        detail: drawdownAmt != null ? {
          label: 'Drawdown',
          value: isAtATH
            ? 'At high'
            : `${formatCurrency(drawdownAmt)} (${drawdownPctVal?.toFixed(1)}%)`,
          color: isAtATH ? colors.chartUp : colors.chartDown,
        } : null,
      });
    }
    return stats;
  }, [currentEquity, allTimeHigh, drawdownAmt, drawdownPctVal, isAtATH, colors]);

  const headlineColor =
    isPnlMode
      ? (headlinePct == null ? colors.text : headlinePct >= 0 ? colors.chartUp : colors.chartDown)
      : colors.text;

  // Snapshots only exist since the account connected; flag ranges that start earlier.
  // A few days of slack covers weekends/holidays at the range edge.
  const dataStartNote = useMemo(() => {
    const requested = equityData?.requestedStart;
    const first = equityData?.equityCurve?.[0]?.time;
    if (!requested || !first) return null;
    const gapDays = (new Date(`${first}T12:00:00`) - new Date(`${requested}T12:00:00`)) / 86400000;
    if (gapDays <= 4) return null;
    const label = new Date(`${first}T12:00:00`).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    return `Data from ${label}`;
  }, [equityData?.requestedStart, equityData?.equityCurve]);

  const headlinePrimarySize = isMobile ? '1.75rem' : '2rem';
  const headlineSecondarySize = isMobile ? '1rem' : '1.15rem';

  const renderChartHeadline = () => {
    if (isPnlMode && (headlinePnlAmount != null || headlinePct != null)) {
      return (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            gap: 2,
            lineHeight: 1.15,
          }}
        >
          {!hideInnerHeadlineLabel && (
            <span
              style={{
                fontSize: isMobile ? '0.8rem' : '0.875rem',
                fontWeight: 500,
                color: colors.textMuted,
                whiteSpace: 'nowrap',
              }}
            >
              P&L
              {dataStartNote && (
                <span title="No daily snapshots before this date (account not yet connected)" style={{ marginLeft: 6, fontWeight: 400, fontSize: '0.75rem' }}>
                  · {dataStartNote}
                </span>
              )}
            </span>
          )}
          {hideInnerHeadlineLabel && dataStartNote && (
            <span style={{ fontSize: '0.75rem', color: colors.textMuted }}>{dataStartNote}</span>
          )}
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'baseline',
              gap: '2px 8px',
            }}
          >
            <span style={{ display: 'inline-flex', alignItems: 'baseline', gap: 8, whiteSpace: 'nowrap' }}>
              {headlinePnlAmount != null && (
                <span
                  style={{
                    fontSize: isMobile ? '1.35rem' : headlinePrimarySize,
                    fontWeight: 700,
                    color: headlineColor,
                    fontVariantNumeric: 'tabular-nums',
                  }}
                >
                  {formatCurrency(headlinePnlAmount)}
                </span>
              )}
              {headlinePct != null && (
                <span
                  style={{
                    fontSize: isMobile ? '1.05rem' : headlineSecondarySize,
                    fontWeight: 700,
                    color: headlineColor,
                    fontVariantNumeric: 'tabular-nums',
                  }}
                >
                  {headlinePct >= 0 ? '+' : ''}
                  {headlinePct.toFixed(2)}%
                </span>
              )}
            </span>
            {scrubDateLabel && (
              <span style={{ fontSize: '0.75rem', fontWeight: 600, color: colors.textMuted }}>
                {scrubDateLabel}
              </span>
            )}
          </div>
        </div>
      );
    }

    if (!isPnlMode && focusPoint?.equity != null) {
      const equityLabel = `$${Number(focusPoint.equity).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
      return (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            gap: 2,
            lineHeight: 1.15,
          }}
        >
          {!hideInnerHeadlineLabel && (
            <span
              style={{
                fontSize: isMobile ? '0.8rem' : '0.875rem',
                fontWeight: 500,
                color: colors.textMuted,
                whiteSpace: 'nowrap',
              }}
            >
              Account value
            </span>
          )}
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'baseline',
              gap: 8,
              flexWrap: 'wrap',
            }}
          >
            <span
              style={{
                fontSize: headlinePrimarySize,
                fontWeight: 700,
                color: colors.text,
                fontVariantNumeric: 'tabular-nums',
              }}
            >
              {equityLabel}
            </span>
            {scrubDateLabel && (
              <span style={{ fontSize: '0.75rem', fontWeight: 600, color: colors.textMuted }}>
                {scrubDateLabel}
              </span>
            )}
          </span>
        </div>
      );
    }

    return null;
  };

  if (!chartData.length) {
    return (
      <div
        style={{
          background: colors.bgCard,
          border: `1px solid ${colors.border}`,
          borderRadius: '12px',
          padding: isMobile ? '8px 6px' : '16px',
          marginBottom: '24px',
        }}
      >
        {(!hideInnerHeadlineLabel || showInlineMobileTimeframe || showHeaderModeToggle) && (
          <div
            data-testid="performance-chart-mode-header"
            style={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 12,
              marginBottom: 8,
            }}
          >
            <div style={{ minWidth: 0, flex: 1 }}>
              {!hideInnerHeadlineLabel && (
                <span style={{ fontSize: '0.875rem', fontWeight: 500, color: colors.textMuted }}>
                  Account value
                </span>
              )}
            </div>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                flexShrink: 0,
                marginLeft: 'auto',
              }}
            >
              {showInlineMobileTimeframe && (
                <PerformanceTimeframeBar
                  timeframe={timeframe}
                  dateRange={dateRange}
                  onChange={onTimeframeChange}
                  colors={colors}
                  isMobile={isMobile}
                  align="right"
                  compact
                />
              )}
              {showHeaderModeToggle && (
                <ChartModeToggle mode={chartMode} onChange={setChartMode} colors={colors} isMobile={isMobile} />
              )}
            </div>
          </div>
        )}
        <div style={{ fontSize: '0.875rem', color: colors.textMuted }}>
          {isLoading
            ? 'Loading equity curve…'
            : 'Equity curve will appear once daily account snapshots are recorded.'}
        </div>
      </div>
    );
  }

  return (
    <div
      style={{
        background: colors.bgCard,
        border: `1px solid ${colors.border}`,
        borderRadius: '12px',
        padding: isMobile ? '8px 10px' : '20px',
        marginBottom: '24px',
        ...cardStyle,
      }}
    >
      <div
        data-testid="performance-chart-mode-header"
        style={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: isMobile ? 10 : 16,
          marginBottom: '12px',
          paddingLeft: isMobile ? '8px' : 0,
          paddingRight: isMobile ? '8px' : 0,
          minWidth: 0,
        }}
      >
        <div style={{ minWidth: 0, flex: 1 }}>
          {renderChartHeadline()}
        </div>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            flexShrink: 0,
            marginLeft: 'auto',
          }}
        >
          {showInlineMobileTimeframe && (
            <PerformanceTimeframeBar
              timeframe={timeframe}
              dateRange={dateRange}
              onChange={onTimeframeChange}
              colors={colors}
              isMobile={isMobile}
              align="right"
              compact
            />
          )}
          {showHeaderModeToggle && (
            <ChartModeToggle mode={chartMode} onChange={setChartMode} colors={colors} isMobile={isMobile} />
          )}
        </div>
      </div>

      <EquityCurvePlot
        chartData={chartData}
        mode={chartMode}
        colors={colors}
        isMobile={isMobile}
        height={isMobile ? 220 : 260}
        timeframe={timeframe}
        showBenchmark={showBenchmark}
        benchmarkSymbol={benchmarkSymbol}
        benchmarkEndPct={focusPoint?.benchmarkPct ?? equityData?.benchmarkPnlPct}
        showDrawdown={showDrawdown}
        onToggleDrawdown={() => setShowDrawdown((v) => !v)}
        drawdownColor={drawdownColor}
        drawdownEnd={focusPoint?.drawdown}
        onActivePointChange={setScrubPoint}
        transfers={transfers}
        legendTrailing={isPnlMode && canOverlayBenchmark ? (
          <BenchmarkSelect
            value={benchmark}
            onChange={onBenchmarkChange}
            colors={colors}
            isMobile={isMobile}
          />
        ) : null}
        unavailableNote={
          isPnlMode && canOverlayBenchmark && !benchmarkAvailable && !benchmarkLoading
            ? `Index comparison unavailable — check market data access for ${benchmarkSymbol}.`
            : null
        }
        rangeNote={
          equityData?.rangeStart && equityData?.rangeEnd
            ? `Range: ${formatRangeDate(equityData.rangeStart)} – ${formatRangeDate(equityData.rangeEnd)}`
            : null
        }
      />

      {equitySummaryStats.length > 0 && (
        <div
          style={{
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row',
            alignItems: isMobile ? 'stretch' : 'center',
            gap: 0,
            borderTop: `1px solid ${colors.border}`,
            marginTop: 8,
          }}
        >
          {equitySummaryStats.map((stat, i) => {
            const isLast = i === equitySummaryStats.length - 1;
            return (
              <div
                key={stat.label}
                style={{
                  flex: isMobile ? 'none' : 1,
                  minWidth: 0,
                  display: isMobile ? 'flex' : 'block',
                  alignItems: isMobile ? 'flex-start' : undefined,
                  justifyContent: isMobile ? 'space-between' : undefined,
                  gap: isMobile ? 12 : undefined,
                  padding: isMobile ? '8px 14px' : '8px 16px',
                  borderRight: !isMobile && !isLast ? `1px solid ${colors.border}` : undefined,
                  borderBottom: isMobile && !isLast ? `1px solid ${colors.border}` : undefined,
                }}
              >
                <div
                  style={{
                    fontSize: isMobile ? '0.8rem' : '0.8125rem',
                    fontWeight: 600,
                    letterSpacing: '0.02em',
                    textTransform: 'uppercase',
                    color: colors.text,
                    marginBottom: isMobile ? 0 : 4,
                    opacity: 0.85,
                  }}
                >
                  {stat.label}
                </div>
                <div style={{ textAlign: isMobile ? 'right' : undefined }}>
                  <div
                    style={{
                      fontSize: isMobile ? '1rem' : '1.05rem',
                      fontWeight: 700,
                      color: stat.color,
                      whiteSpace: 'nowrap',
                      fontVariantNumeric: 'tabular-nums',
                    }}
                  >
                    {stat.value}
                  </div>
                  {stat.detail && (
                    <div style={{ marginTop: 2, fontSize: '0.75rem', fontWeight: 650, color: stat.detail.color, fontVariantNumeric: 'tabular-nums' }}>
                      {stat.detail.label} {stat.detail.value}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

/** Trade distribution histogram — horizontal bars (thin rows) */
const TradeDistributionChart = memo(({ distribution, colors, titleOutside = false, isMobile = false }) => {
  const chartData = useMemo(() => {
    const bins = distribution?.bins ?? [];
    return bins.map((b) => ({ ...b, rangeLabel: b.displayRange ?? `$${b.range}` }));
  }, [distribution]);

  const chartHeight = Math.min(280, Math.max(140, chartData.length * 26 + 20));

  return (
    <>
      {titleOutside && (
        <DashboardSectionTitle title="Trade Distribution" marginBottom={10} />
      )}
    <div
      style={{
        background: colors.bgCard,
        border: `1px solid ${colors.border}`,
        borderRadius: '12px',
        padding: isMobile ? '14px 12px' : '20px',
        marginBottom: isMobile ? '20px' : '24px',
      }}
    >
      {!titleOutside && (
      <div style={{ fontSize: '0.875rem', color: colors.textMuted, textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '16px' }}>
        Trade Distribution
      </div>
      )}
      <ResponsiveContainer width="100%" height={chartHeight}>
        <BarChart
          data={chartData}
          layout="vertical"
          margin={{ top: 4, right: 40, left: 4, bottom: 4 }}
          barCategoryGap="28%"
        >
          <CartesianGrid strokeDasharray="3 3" stroke={colors.border} opacity={0.5} horizontal={false} />
          <XAxis
            type="number"
            stroke={colors.textMuted}
            tick={{ fontSize: 11 }}
            allowDecimals={false}
          />
          <YAxis
            type="category"
            dataKey="rangeLabel"
            stroke={colors.textMuted}
            tick={{ fontSize: 11 }}
            width={108}
          />
          <RechartsTooltip content={() => null} />
          <Bar dataKey="count" radius={[0, 3, 3, 0]} isAnimationActive={false} barSize={12} maxBarSize={14}>
            <LabelList
              dataKey="count"
              position="right"
              formatter={(value) => `${value}`}
              style={{ fill: colors.text, fontSize: 11 }}
            />
            {chartData.map((entry, i) => {
              const r = (entry.range ?? '').toLowerCase();
              const isLoss =
                entry.side === 'loss' ||
                (!entry.side && (r.includes('-') || r.startsWith('under')));
              return <Cell key={i} fill={isLoss ? colors.chartDown : colors.chartUp} />;
            })}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
    </>
  );
});

const formatTradeDuration = (mins) => {
  if (mins == null || mins < 0) return '—';
  if (mins < 60) return `${mins}m`;
  if (mins < 1440) {
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    return m ? `${h}h ${m}m` : `${h}h`;
  }
  const d = Math.floor(mins / 1440);
  const h = Math.floor((mins % 1440) / 60);
  return h ? `${d}d ${h}h` : `${d}d`;
};

const formatTradeTime = (str) => {
  if (!str) return '—';
  const d = new Date(str);
  return d.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
};

const formatTradeDate = (str) => {
  if (!str) return '—';
  const d = new Date(str);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const TradeStatusBadge = ({ isOpen, colors }) => (
  <span
    style={{
      flexShrink: 0,
      padding: '2px 8px',
      fontSize: '0.65rem',
      fontWeight: 700,
      borderRadius: 4,
      lineHeight: 1.25,
      letterSpacing: '0.03em',
      backgroundColor: isOpen ? colors.primary : 'rgba(255, 255, 255, 0.08)',
      color: isOpen ? '#ffffff' : colors.textMuted,
      border: isOpen ? `1px solid ${colors.primary}` : `1px solid ${colors.border}`,
      boxShadow: isOpen ? `0 0 0 1px ${colors.primary}33` : 'none',
    }}
  >
    {isOpen ? 'OPEN' : 'CLOSED'}
  </span>
);

const TRADE_CONFIDENCE_LABELS = {
  full: 'Full',
  partial: 'Partial',
  carry_over: 'Carry-over',
  entry_reconstructed: 'Reconstructed',
  inconsistent: 'Inconsistent',
};

const TRADE_CONFIDENCE_ABBR = {
  full: 'F',
  partial: 'P',
  carry_over: 'C',
  entry_reconstructed: 'R',
  inconsistent: 'I',
};

const tradeConfidenceStyle = (level, colors) => {
  if (level === 'full') {
    return {
      backgroundColor: 'rgba(74, 222, 128, 0.12)',
      color: colors.chartUp,
      border: '1px solid rgba(74, 222, 128, 0.35)',
    };
  }
  if (level === 'carry_over' || level === 'entry_reconstructed') {
    return {
      backgroundColor: 'rgba(251, 191, 36, 0.12)',
      color: '#fbbf24',
      border: '1px solid rgba(251, 191, 36, 0.35)',
    };
  }
  return {
    backgroundColor: 'rgba(248, 113, 113, 0.12)',
    color: colors.chartDown,
    border: '1px solid rgba(248, 113, 113, 0.35)',
  };
};

const TradeConfidenceBadge = ({ confidence, colors, isSuperuser = false, compact = false }) => {
  const level = confidence || 'full';
  if (!isSuperuser && level === 'full') return null;

  const label = TRADE_CONFIDENCE_LABELS[level] || level;
  const style = tradeConfidenceStyle(level, colors);

  return (
    <span
      title={`Data confidence: ${label}`}
      style={{
        flexShrink: 0,
        padding: compact ? '0px 4px' : '2px 6px',
        fontSize: compact ? '0.6rem' : '0.6rem',
        fontWeight: 700,
        borderRadius: 4,
        lineHeight: 1.25,
        letterSpacing: '0.02em',
        textTransform: 'uppercase',
        ...style,
      }}
    >
      {compact ? (TRADE_CONFIDENCE_ABBR[level] || label.charAt(0)) : label}
    </span>
  );
};

const DIRECTION_LABELS = {
  Long: 'Long',
  Short: 'Short',
};

const TradeDirectionBadge = ({ direction, symbol, colors }) => {
  if (!direction) return <span style={{ color: colors.textMuted }}>—</span>;
  const isShort = String(direction).toLowerCase() === 'short';
  const label = tradeDirectionLabel(DIRECTION_LABELS[direction] || direction, symbol);
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        padding: '2px 8px',
        fontSize: '0.65rem',
        fontWeight: 700,
        borderRadius: 4,
        lineHeight: 1.25,
        letterSpacing: '0.03em',
        textTransform: 'uppercase',
        backgroundColor: isShort ? 'rgba(248, 113, 113, 0.12)' : 'rgba(74, 222, 128, 0.12)',
        color: isShort ? colors.chartDown : colors.chartUp,
        border: `1px solid ${isShort ? 'rgba(248, 113, 113, 0.35)' : 'rgba(74, 222, 128, 0.35)'}`,
      }}
    >
      {label}
    </span>
  );
};

const formatFillOrderType = (orderType) => {
  if (!orderType) return null;
  const u = String(orderType).toUpperCase();
  if (u.includes('STOP')) return 'Stop';
  if (u.includes('LIMIT')) return 'Limit';
  if (u.includes('MARKET')) return 'Market';
  return orderType;
};

/** Chronological order/fill timeline for an expanded trade row. */
const TradeOrderTimeline = ({ fills, colors }) => {
  if (!fills?.length) {
    return (
      <p style={{ margin: 0, fontSize: '0.78rem', color: colors.textMuted }}>
        No order history recorded for this trade yet.
      </p>
    );
  }

  return (
    <div style={{ padding: '2px 0' }}>
      {fills.map((fill, idx) => {
        const isEntry = fill.side === 'entry';
        const accent = isEntry ? colors.chartUp : colors.chartDown;
        const typeLabel = formatFillOrderType(fill.orderType);
        const isLast = idx === fills.length - 1;

        return (
          <div
            key={`${fill.orderId || 'fill'}-${fill.executedAt || idx}`}
            style={{
              display: 'flex',
              gap: 12,
              paddingBottom: isLast ? 0 : 12,
            }}
          >
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                width: 12,
                flexShrink: 0,
              }}
            >
              <div
                style={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  backgroundColor: accent,
                  marginTop: 5,
                  flexShrink: 0,
                  boxShadow: `0 0 0 2px ${accent}33`,
                }}
              />
              {!isLast && (
                <div
                  style={{
                    flex: 1,
                    width: 2,
                    backgroundColor: colors.border,
                    marginTop: 4,
                    minHeight: 24,
                  }}
                />
              )}
            </div>
            <div style={{ flex: 1, minWidth: 0, paddingBottom: isLast ? 0 : 2 }}>
              <div
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: '4px 10px',
                  alignItems: 'baseline',
                  fontSize: '0.78rem',
                }}
              >
                <span style={{ fontWeight: 700, color: accent }}>
                  {isEntry ? 'Entry' : 'Exit'}
                </span>
                {fill.qty != null && (
                  <span style={{ color: colors.text }}>
                    {fill.qty} @ {formatTradePrice(fill.price)}
                  </span>
                )}
                {typeLabel && (
                  <span
                    style={{
                      color: colors.textMuted,
                      fontSize: '0.72rem',
                      padding: '1px 6px',
                      borderRadius: 4,
                      border: `1px solid ${colors.border}`,
                    }}
                  >
                    {typeLabel}
                  </span>
                )}
                {fill.orderId && (
                  <span
                    style={{
                      color: colors.textMuted,
                      fontFamily: 'ui-monospace, monospace',
                      fontSize: '0.7rem',
                    }}
                  >
                    Order {fill.orderId}
                  </span>
                )}
              </div>
              <div style={{ fontSize: '0.72rem', color: colors.textMuted, marginTop: 3 }}>
                {formatTradeTime(fill.executedAt)}
                {fill.commission > 0 ? ` · comm ${formatCurrency(fill.commission)}` : ''}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

const ExpandTradeButton = ({ expanded, onClick, colors, label }) => (
  <button
    type="button"
    aria-expanded={expanded}
    aria-label={label || (expanded ? 'Collapse order history' : 'Expand order history')}
    onClick={onClick}
    style={{
      background: 'none',
      border: 'none',
      padding: 0,
      cursor: 'pointer',
      color: colors.textMuted,
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
    }}
  >
    <ChevronDown
      size={16}
      style={{
        transform: expanded ? 'rotate(180deg)' : 'none',
        transition: 'transform 0.2s',
      }}
    />
  </button>
);

const TRADES_SORT_OPTIONS = [
  { field: 'openDate', label: 'Open date' },
  { field: 'closeDate', label: 'Close date' },
  { field: 'journaled', label: 'Journaled' },
  { field: 'notJournaled', label: 'Not journaled' },
  { field: 'symbol', label: 'Symbol' },
  { field: 'pnl', label: 'P&L' },
];

function tradeSortValue(row, field, journaledTradeIdSet) {
  switch (field) {
    case 'symbol':
      return (row.symbol || '').toUpperCase();
    case 'openDate': {
      const d = row.entryTime || row.openedAt || row.tradeDate;
      return d ? new Date(d).getTime() : 0;
    }
    case 'closeDate': {
      const d = row.exitTime || row.closedAt || (row.status === 'open' ? null : row.tradeDate);
      return d ? new Date(d).getTime() : 0;
    }
    case 'journaled': {
      const journaled = row.tradeId && journaledTradeIdSet?.has(row.tradeId);
      return journaled ? 1 : 0;
    }
    case 'notJournaled': {
      const journaled = row.tradeId && journaledTradeIdSet?.has(row.tradeId);
      return journaled ? 0 : 1;
    }
    case 'tradeDate': {
      // legacy alias
      const d = row.tradeDate || row.exitTime || row.entryTime;
      return d ? new Date(d).getTime() : 0;
    }
    case 'pnl':
      return row.pnl != null && !Number.isNaN(row.pnl) ? Number(row.pnl) : -Infinity;
    case 'entryPrice':
      return row.entryPrice != null ? Number(row.entryPrice) : -Infinity;
    case 'exitPrice':
      return row.exitPrice != null ? Number(row.exitPrice) : -Infinity;
    case 'durationMinutes':
      return row.durationMinutes != null ? Number(row.durationMinutes) : -Infinity;
    case 'status':
      return row.status === 'open' ? 0 : 1;
    default:
      return '';
  }
}

function compareTrades(a, b, sortField, sortDirection, journaledTradeIdSet) {
  const dir = sortDirection === 'asc' ? 1 : -1;
  const aVal = tradeSortValue(a, sortField, journaledTradeIdSet);
  const bVal = tradeSortValue(b, sortField, journaledTradeIdSet);
  if (typeof aVal === 'number' && typeof bVal === 'number') {
    return (aVal - bVal) * dir;
  }
  return String(aVal).localeCompare(String(bVal)) * dir;
}

/** Open trades always first; closed trades follow, each group sorted by the active field. */
function sortTrades(rows, sortField, sortDirection, journaledTradeIdSet) {
  const open = [];
  const closed = [];
  for (const row of rows) {
    if (row.status === 'open') open.push(row);
    else closed.push(row);
  }
  open.sort((a, b) => compareTrades(a, b, sortField, sortDirection, journaledTradeIdSet));
  closed.sort((a, b) => compareTrades(a, b, sortField, sortDirection, journaledTradeIdSet));
  return [...open, ...closed];
}

const SlTpPills = ({ trade, colors }) => (
  <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', alignItems: 'center' }}>
    <span
      title={
        trade.hadStopLoss
          ? (trade.stopLossPrice != null ? `Stop @ ${trade.stopLossPrice}` : 'Had stop loss')
          : 'No stop loss recorded'
      }
      style={{
        fontSize: '0.65rem',
        fontWeight: 700,
        letterSpacing: '0.03em',
        padding: '1px 5px',
        borderRadius: 4,
        backgroundColor: trade.hadStopLoss ? 'rgba(7, 203, 163, 0.15)' : `${colors.border}88`,
        color: trade.hadStopLoss ? (colors.chartUp || '#07CBA3') : colors.textMuted,
      }}
    >
      SL
    </span>
    <span
      title={
        trade.hadTakeProfit
          ? (trade.takeProfitPrice != null ? `TP @ ${trade.takeProfitPrice}` : 'Had take profit')
          : 'No take profit recorded'
      }
      style={{
        fontSize: '0.65rem',
        fontWeight: 700,
        letterSpacing: '0.03em',
        padding: '1px 5px',
        borderRadius: 4,
        backgroundColor: trade.hadTakeProfit ? 'rgba(7, 203, 163, 0.15)' : `${colors.border}88`,
        color: trade.hadTakeProfit ? (colors.chartUp || '#07CBA3') : colors.textMuted,
      }}
    >
      TP
    </span>
  </div>
);

const formatTradePrice = (price) => (price != null ? `$${Number(price).toFixed(2)}` : '—');

const formatTradePercent = (value) => {
  if (value == null || Number.isNaN(value)) return '—';
  const num = parseFloat(value);
  return `${num >= 0 ? '+' : ''}${num.toFixed(2)}%`;
};

/** Wide viewports: table; narrow/mobile: compact cards (sidebar + dashboard padding eat horizontal space). */
const RECENT_TRADES_COMPACT_MAX_WIDTH = 1200;

const RecentPositionsTable = ({
  accountId,
  paperTrading,
  livePositions,
  calculateUnrealizedPL,
  colors,
  isMobile,
  isSuperuser = false,
  onSymbolClick,
  journaledTradeIdSet,
  tradeIdToJournalMap,
  onSyncClick,
  onSettingsClick,
  showAdminButtons,
  includeOpenPositions = true,
  onIncludeOpenPositionsChange,
  titleOutside = false,
}) => {
  const isNarrowViewport = useMaxWidth(RECENT_TRADES_COMPACT_MAX_WIDTH);
  const useCompactLayout = isMobile || isNarrowViewport;

  const [expandedCards, setExpandedCards] = useState(new Set());
  const [sortField, setSortField] = useState('closeDate');
  const [sortDirection, setSortDirection] = useState('desc');
  const [symbolSearch, setSymbolSearch] = useState('');
  const [symbolDebounced, setSymbolDebounced] = useState('');
  const loadMoreRef = useRef(null);

  useEffect(() => {
    const t = setTimeout(() => {
      setSymbolDebounced(symbolSearch.trim());
    }, 300);
    return () => clearTimeout(t);
  }, [symbolSearch]);

  const {
    data: recentData,
    isLoading: recentLoading,
    isFetching: recentFetching,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  } = usePerformanceRecentTrades({
    accountId,
    paperTrading,
    symbol: symbolDebounced,
    limit: RECENT_TRADES_PAGE_SIZE,
    includeOpenPositions,
    sortBy: sortField,
    sortDir: sortDirection,
    enabled: !!accountId,
  });

  useEffect(() => {
    const el = loadMoreRef.current;
    if (!el) return undefined;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage();
        }
      },
      { rootMargin: '240px' }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage, recentData?.pages?.length]);

  const rows = useMemo(() => {
    const list = recentData?.pages?.flatMap((page) => page?.positions ?? []) ?? [];
    if (!list.length) return list;

    const filtered = includeOpenPositions
      ? list
      : list.filter((p) => p.status !== 'open');

    const liveBySymbol = new Map();
    for (const pos of livePositions || []) {
      if (accountId && pos.AccountID && pos.AccountID !== accountId) continue;
      const sym = (pos.Symbol || '').toUpperCase();
      if (sym) liveBySymbol.set(sym, pos);
    }

    return filtered.map((p) => {
      if (p.status !== 'open') return p;
      if (p.pnl != null && !Number.isNaN(p.pnl)) return p;
      const live = liveBySymbol.get((p.symbol || '').toUpperCase());
      if (!live) return p;
      return { ...p, pnl: parseFloat(calculateUnrealizedPL(live).toFixed(2)) };
    });
  }, [recentData?.pages, livePositions, accountId, calculateUnrealizedPL, includeOpenPositions]);

  const total = recentData?.pages?.[0]?.total ?? 0;

  // Within-page refine: open-first + live PnL enrichment. Cross-page order comes from sortBy/sortDir on the API.
  const sortedRows = useMemo(
    () => sortTrades(rows, sortField, sortDirection, journaledTradeIdSet),
    [rows, sortField, sortDirection, journaledTradeIdSet]
  );

  const includeOpenPositionsControl = onIncludeOpenPositionsChange ? (
    <div
      className="recent-trades-include-open-check"
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
        flexShrink: 0,
        '--form-check-bg': colors.bgPrimary,
        '--form-check-border': colors.border,
        '--form-check-primary': colors.primary,
        '--form-check-label': colors.text,
      }}
    >
      <Form.Check
        type="checkbox"
        id="performance-include-open-positions"
        checked={includeOpenPositions}
        onChange={(e) => onIncludeOpenPositionsChange(e.target.checked)}
        label={
          <span
            style={{
              color: colors.text,
              fontSize: '0.85rem',
              whiteSpace: 'nowrap',
              fontWeight: 500,
              lineHeight: 1.25,
              display: 'inline-block',
            }}
          >
            Include open positions
          </span>
        }
        title="Includes open positions in stats, distribution, and the trades list"
        style={{ marginBottom: 0 }}
      />
    </div>
  ) : null;

  const getPnlColor = (value) => {
    if (value == null || Number.isNaN(value)) return colors.chartNeutral;
    const num = parseFloat(value);
    if (num === 0) return colors.chartNeutral;
    return num >= 0 ? colors.chartUp : colors.chartDown;
  };

  const toggleCardExpand = (key) => {
    setExpandedCards((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const cardShellStyle = {
    border: `1px solid ${colors.border}`,
    borderLeft: `3px solid ${colors.primary}`,
    backgroundColor: colors.bgSecondary,
    marginBottom: '24px',
  };

  const renderSortToolbar = () => (
    <div
      role="toolbar"
      aria-label="Sort and filter recent trades"
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        flexWrap: 'wrap',
        minHeight: 36,
        width: '100%',
      }}
    >
      <Form.Control
        type="search"
        size="sm"
        placeholder="Search symbol"
        value={symbolSearch}
        onChange={(e) => setSymbolSearch(e.target.value)}
        aria-label="Search trades by symbol"
        style={{
          flex: '1 1 140px',
          minWidth: 120,
          maxWidth: 280,
          fontSize: '0.8rem',
          backgroundColor: colors.bgPrimary,
          borderColor: colors.border,
          color: colors.text,
        }}
      />
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          flexShrink: 0,
          marginLeft: 'auto',
        }}
        role="group"
        aria-label="Sort trades by field"
      >
        <div style={{ flex: '0 1 auto', minWidth: 0, maxWidth: useCompactLayout ? 220 : 200 }}>
          <ThemedSelect
            value={sortField}
            onChange={(v) => setSortField(v)}
            options={TRADES_SORT_OPTIONS.map(({ field, label }) => ({ value: field, label }))}
            width="100%"
            minWidth={useCompactLayout ? 160 : 140}
          />
        </div>
        <button
          type="button"
          title={sortDirection === 'asc' ? 'Ascending — click for descending' : 'Descending — click for ascending'}
          aria-label={sortDirection === 'asc' ? 'Sort ascending' : 'Sort descending'}
          onClick={() => setSortDirection((d) => (d === 'asc' ? 'desc' : 'asc'))}
          style={{
            width: 36,
            height: 36,
            flexShrink: 0,
            borderRadius: 8,
            border: `1px solid ${colors.border}`,
            backgroundColor: colors.bgPrimary,
            color: colors.textMuted,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 0,
            cursor: 'pointer',
          }}
        >
          {sortDirection === 'asc' ? <SortUp size={18} /> : <SortDown size={18} />}
        </button>
      </div>
    </div>
  );

  const renderSymbolLink = (symbol, size = '0.88rem') => (
    <strong
      role="button"
      tabIndex={0}
      onClick={() => onSymbolClick?.(symbol)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onSymbolClick?.(symbol);
        }
      }}
      style={{
        fontSize: size,
        fontWeight: 700,
        color: colors.primary,
        cursor: onSymbolClick ? 'pointer' : 'default',
        whiteSpace: 'nowrap',
      }}
      title={onSymbolClick ? `Open chart for ${symbol}` : undefined}
    >
      {symbol}
    </strong>
  );

  const getExpandKey = (p, i) =>
    p.tradeId || `${p.symbol}|${p.entryTime || ''}|${p.exitTime || p.tradeDate || ''}|${i}`;

  const renderDesktopTable = () => (
    <Table
      bordered
      hover
      size="sm"
      className="recent-trades-table"
      style={{
        fontSize: '0.8rem',
        marginBottom: 0,
        backgroundColor: colors.bgPrimary,
        color: colors.text,
        '--bs-table-bg': colors.bgPrimary,
        '--bs-table-color': colors.text,
        '--bs-table-hover-bg': colors.bgSecondary,
        '--bs-table-hover-color': colors.text,
        '--bs-table-border-color': colors.border,
      }}
    >
      <thead>
        <tr style={{ fontSize: '0.72rem', color: colors.textMuted, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
          <th style={{ width: 32, color: colors.textMuted }} aria-label="Expand" />
          <th style={{ color: colors.textMuted }}>Symbol</th>
          <th style={{ color: colors.textMuted }}>Status</th>
          <th style={{ color: colors.textMuted }}>Direction</th>
          <th style={{ color: colors.textMuted }}>SL / TP</th>
          <th style={{ color: colors.textMuted }}>Date</th>
          <th style={{ color: colors.textMuted }}>Entry</th>
          <th style={{ color: colors.textMuted }}>Exit</th>
          <th style={{ color: colors.textMuted }}>Qty</th>
          <th style={{ color: colors.textMuted }}>Duration</th>
          <th style={{ textAlign: 'right', color: colors.textMuted }}>P&L</th>
          <th style={{ width: 40, color: colors.textMuted }} aria-label="Journal" />
        </tr>
      </thead>
      <tbody>
        {sortedRows.map((p, i) => {
          const isOpen = p.status === 'open';
          const hasPnl = p.pnl != null && !Number.isNaN(p.pnl);
          const pnlValue = hasPnl ? p.pnl : 0;
          const pnlPct = typeof p.pnlPct === 'number' ? p.pnlPct : computeTradePnlPercent(p);
          const pnlColor = getPnlColor(hasPnl ? pnlValue : null);
          const displayDate = p.tradeDate || (isOpen ? p.entryTime : p.exitTime);
          const expandKey = getExpandKey(p, i);
          const rowKey = expandKey;
          const isExpanded = expandedCards.has(expandKey);

          return (
            <React.Fragment key={rowKey}>
              <tr>
                <td style={{ verticalAlign: 'middle', width: 32, paddingLeft: 10, paddingRight: 4 }}>
                  <ExpandTradeButton
                    expanded={isExpanded}
                    onClick={() => toggleCardExpand(expandKey)}
                    colors={colors}
                  />
                </td>
                <td style={{ verticalAlign: 'middle', whiteSpace: 'nowrap' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                    {renderSymbolLink(p.symbol, '0.8rem')}
                    <TradeConfidenceBadge
                      confidence={p.dataConfidence}
                      colors={colors}
                      isSuperuser={isSuperuser}
                      compact
                    />
                  </div>
                </td>
                <td style={{ verticalAlign: 'middle' }}>
                  <TradeStatusBadge isOpen={isOpen} colors={colors} />
                </td>
                <td style={{ verticalAlign: 'middle', whiteSpace: 'nowrap' }}>
                  <TradeDirectionBadge direction={p.direction} symbol={p.symbol} colors={colors} />
                </td>
                <td style={{ verticalAlign: 'middle' }}>
                  <SlTpPills trade={p} colors={colors} />
                </td>
                <td style={{ verticalAlign: 'middle', whiteSpace: 'nowrap' }}>
                  <div style={{ fontWeight: 600, color: colors.text }}>{formatTradeDate(displayDate)}</div>
                  <div style={{ fontSize: '0.72rem', color: colors.textMuted }}>{formatTradeTime(displayDate)}</div>
                </td>
                <td style={{ verticalAlign: 'middle', whiteSpace: 'nowrap', color: colors.text }}>{formatTradePrice(p.entryPrice)}</td>
                <td style={{ verticalAlign: 'middle', whiteSpace: 'nowrap', color: colors.text }}>
                  {isOpen ? '—' : formatTradePrice(p.exitPrice)}
                </td>
                <td style={{ verticalAlign: 'middle', color: colors.text }}>{p.quantity ?? '—'}</td>
                <td style={{ verticalAlign: 'middle', whiteSpace: 'nowrap', color: colors.text }}>{formatTradeDuration(p.durationMinutes)}</td>
                <td style={{ verticalAlign: 'middle', textAlign: 'right', whiteSpace: 'nowrap' }}>
                  <div style={{ fontWeight: 700, color: pnlColor }}>
                    {hasPnl ? formatCurrency(pnlValue) : '—'}
                  </div>
                  {pnlPct != null && (
                    <div style={{ fontSize: '0.72rem', color: pnlColor, opacity: 0.95 }}>
                      {formatTradePercent(pnlPct)}
                    </div>
                  )}
                </td>
                <td style={{ verticalAlign: 'middle', textAlign: 'center', width: 40, padding: '0 6px' }}>
                  {p.tradeId && (
                    <button
                        onClick={() => {
                          const positionData = {
                            Symbol: p.symbol,
                            Direction: tradeDirectionLabel(p.direction, p.symbol),
                            EntryPrice: p.entryPrice,
                            ExitPrice: p.exitPrice,
                            Quantity: p.quantity,
                            OpenedDateTime: p.entryTime,
                            ClosedDateTime: p.exitTime,
                            RealizedPnL: p.pnl,
                          };
                          const existingJournal = tradeIdToJournalMap?.get(p.tradeId);
                          window.dispatchEvent(new CustomEvent('tradeJournal:open', {
                            detail: existingJournal
                              ? { journal: existingJournal }
                              : { positionData, tradeId: p.tradeId },
                          }));
                        }}
                      title={journaledTradeIdSet.has(p.tradeId) ? 'View journal' : 'Add journal'}
                      style={{
                        background: 'none',
                        border: 'none',
                        padding: '2px 4px',
                        cursor: 'pointer',
                        color: journaledTradeIdSet.has(p.tradeId) ? colors.chartUp : colors.textMuted,
                        opacity: 0.85,
                        lineHeight: 1,
                      }}
                    >
                      {journaledTradeIdSet.has(p.tradeId) ? <BookmarkFill size={13} /> : <Bookmark size={13} />}
                    </button>
                  )}
                </td>
              </tr>
              {isExpanded && (
                <tr className="recent-trades-detail-row">
                  <td
                    colSpan={12}
                    style={{
                      backgroundColor: colors.bgSecondary,
                      borderColor: colors.border,
                      padding: '12px 16px 14px',
                    }}
                  >
                    <div
                      style={{
                        fontSize: '0.68rem',
                        fontWeight: 600,
                        color: colors.textMuted,
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        marginBottom: 10,
                      }}
                    >
                      Order timeline
                      {p.fills?.length ? ` · ${p.fills.length} fill${p.fills.length === 1 ? '' : 's'}` : ''}
                    </div>
                    {(isSuperuser || (p.dataConfidence && p.dataConfidence !== 'full')) && (
                      <p style={{ margin: '0 0 8px', fontSize: '0.72rem', color: colors.textMuted }}>
                        Data confidence: {TRADE_CONFIDENCE_LABELS[p.dataConfidence] || p.dataConfidence || 'Full'}
                        {p.incompleteReason ? ` · ${p.incompleteReason.replace(/_/g, ' ')}` : ''}
                      </p>
                    )}
                    <TradeOrderTimeline fills={p.fills} colors={colors} />
                  </td>
                </tr>
              )}
            </React.Fragment>
          );
        })}
      </tbody>
    </Table>
  );

  const tradeGridColumns = 'minmax(4.5rem, auto) minmax(5.5rem, auto) minmax(4.75rem, 1fr) 28px';

  const renderMobileCards = () => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      {sortedRows.map((p, i) => {
        const isOpen = p.status === 'open';
        const hasPnl = p.pnl != null && !Number.isNaN(p.pnl);
        const pnlValue = hasPnl ? p.pnl : 0;
        const pnlPct = typeof p.pnlPct === 'number' ? p.pnlPct : computeTradePnlPercent(p);
        const pnlColor = getPnlColor(hasPnl ? pnlValue : null);
        const expandKey = getExpandKey(p, i);
        const isExpanded = expandedCards.has(expandKey);
        const displayDate = p.tradeDate || (isOpen ? p.entryTime : p.exitTime);

        return (
          <div
            key={expandKey}
            style={{
              borderRadius: 8,
              border: `1px solid ${colors.borderLight}`,
              backgroundColor: colors.bgPrimary,
              padding: '7px 10px 8px',
            }}
          >
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: tradeGridColumns,
                gap: '6px 8px',
                alignItems: 'center',
              }}
            >
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 4, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                  {renderSymbolLink(p.symbol)}
                  <TradeConfidenceBadge
                    confidence={p.dataConfidence}
                    colors={colors}
                    isSuperuser={isSuperuser}
                    compact
                  />
                </div>
                {/* Only OPEN is worth a badge — "closed" is already implied by the Closed date. */}
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, alignItems: 'center' }}>
                  {isOpen && <TradeStatusBadge isOpen colors={colors} />}
                  <TradeDirectionBadge direction={p.direction} symbol={p.symbol} colors={colors} />
                  <SlTpPills trade={p} colors={colors} />
                </div>
              </div>

              <div style={{ minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
                <span style={{ fontSize: '0.65rem', color: colors.textMuted, letterSpacing: '0.02em' }}>
                  {isOpen ? 'Opened' : 'Closed'}
                </span>
                <span
                  style={{
                    fontSize: '0.8125rem',
                    fontWeight: 600,
                    color: colors.text,
                    lineHeight: 1.15,
                    whiteSpace: 'nowrap',
                  }}
                >
                  {formatTradeDate(displayDate)}
                </span>
              </div>

              <div style={{ minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2, alignItems: 'flex-end' }}>
                <span style={{ fontSize: '0.65rem', color: colors.textMuted, whiteSpace: 'nowrap' }}>
                  {isOpen ? 'Unrealized' : 'Realized'}
                </span>
                {pnlPct != null && (
                  <span
                    style={{
                      fontSize: '0.6875rem',
                      fontWeight: 600,
                      color: pnlColor,
                      whiteSpace: 'nowrap',
                      fontVariantNumeric: 'tabular-nums',
                    }}
                  >
                    {formatTradePercent(pnlPct)}
                  </span>
                )}
                <span
                  style={{
                    fontSize: '0.8125rem',
                    fontWeight: 700,
                    color: pnlColor,
                    whiteSpace: 'nowrap',
                    fontVariantNumeric: 'tabular-nums',
                  }}
                >
                  {hasPnl ? formatCurrency(pnlValue) : '—'}
                </span>
              </div>

              <ExpandTradeButton
                expanded={isExpanded}
                onClick={() => toggleCardExpand(expandKey)}
                colors={colors}
              />
            </div>

            {isExpanded && (
              <div
                style={{
                  marginTop: 8,
                  paddingTop: 8,
                  borderTop: `1px solid ${colors.border}`,
                }}
              >
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: isOpen ? '1fr' : '1fr 1fr',
                    gap: '8px 10px',
                    marginBottom: 8,
                  }}
                >
                  <div style={{ minWidth: 0 }}>
                    <span style={{ fontSize: '0.65rem', color: colors.textMuted, display: 'block' }}>Entry</span>
                    <span
                      style={{
                        fontSize: '0.8125rem',
                        fontWeight: 600,
                        color: colors.text,
                        whiteSpace: 'nowrap',
                        fontVariantNumeric: 'tabular-nums',
                      }}
                    >
                      {formatTradePrice(p.entryPrice)}
                    </span>
                  </div>
                  {!isOpen && (
                    <div style={{ minWidth: 0 }}>
                      <span style={{ fontSize: '0.65rem', color: colors.textMuted, display: 'block' }}>Exit</span>
                      <span
                        style={{
                          fontSize: '0.8125rem',
                          fontWeight: 600,
                          color: colors.text,
                          whiteSpace: 'nowrap',
                          fontVariantNumeric: 'tabular-nums',
                        }}
                      >
                        {formatTradePrice(p.exitPrice)}
                      </span>
                    </div>
                  )}
                </div>
                <div
                  style={{
                    fontSize: '0.6875rem',
                    color: colors.textMuted,
                    marginBottom: 8,
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: '2px 0',
                  }}
                >
                  <span>Qty {p.quantity ?? '—'}</span>
                  <span aria-hidden="true" style={{ margin: '0 5px', opacity: 0.45 }}>·</span>
                  <span>{formatTradeDuration(p.durationMinutes)}</span>
                  {p.direction && (
                    <>
                      <span aria-hidden="true" style={{ margin: '0 5px', opacity: 0.45 }}>·</span>
                      <span>{tradeDirectionLabel(p.direction, p.symbol)}</span>
                    </>
                  )}
                </div>
                <div
                  style={{
                    fontSize: '0.6875rem',
                    fontWeight: 600,
                    color: colors.textMuted,
                    textTransform: 'uppercase',
                    letterSpacing: '0.04em',
                    marginBottom: 8,
                  }}
                >
                  Order timeline
                </div>
                <TradeOrderTimeline fills={p.fills} colors={colors} />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );

  const useMobileFlatSection = titleOutside && isMobile;
  const showTradeCards = useCompactLayout;

  const renderEmptyState = (message) => (
    <div
      style={{
        color: colors.textLight,
        fontSize: '0.875rem',
        padding: useMobileFlatSection || useCompactLayout ? '8px 0' : '16px',
      }}
    >
      {message}
    </div>
  );

  const renderTradeListBody = () => {
    if (recentLoading && !recentData) {
      return renderEmptyState('Loading trades…');
    }
    if (sortedRows.length === 0) {
      return renderEmptyState(
        symbolDebounced
          ? `No trades matching "${symbolDebounced}".`
          : includeOpenPositions
            ? 'No trades yet.'
            : 'No closed trades yet.'
      );
    }
    if (showTradeCards) {
      return renderMobileCards();
    }
    return renderDesktopTable();
  };

  const renderInfiniteScrollFooter = () => {
    if (total <= 0 && !isFetchingNextPage) return null;
    return (
      <div
        ref={loadMoreRef}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: useMobileFlatSection ? '12px 0 0' : (useCompactLayout ? '10px 0 0' : '12px 16px'),
          borderTop: sortedRows.length > 0 ? `1px solid ${colors.border}` : undefined,
          marginTop: sortedRows.length > 0 && !useMobileFlatSection && useCompactLayout ? 8 : 0,
          minHeight: 36,
        }}
      >
        <span style={{ fontSize: '0.75rem', color: colors.textMuted }}>
          {isFetchingNextPage
            ? 'Loading more…'
            : hasNextPage
              ? 'Scroll for more'
              : recentFetching && !recentLoading
                ? 'Updating…'
                : sortedRows.length > 0
                  ? `${sortedRows.length}${total > sortedRows.length ? ` of ${total}` : ''} trades`
                  : ''}
        </span>
      </div>
    );
  };

  if (useMobileFlatSection) {
    return (
      <section
        className="recent-trades-section recent-trades-section--mobile-flat"
        style={{ marginBottom: 24 }}
        aria-label="Trades"
      >
        {/* Spelled out because the surrounding sections are timeframe-scoped and this one is not. */}
        <DashboardSectionTitle
          title="Trades"
          count={total > 0 ? `${total} all-time` : null}
          marginBottom={10}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
            {includeOpenPositionsControl}
            {typeof onSyncClick === 'function' && (
              <PrimaryButton
                variant="compact"
                size="sm"
                onClick={onSyncClick}
                title="Sync trade history"
                style={{ height: 28, minHeight: 28, paddingTop: 0, paddingBottom: 0, fontSize: '0.8rem' }}
              >
                Sync
              </PrimaryButton>
            )}
          </div>
        </DashboardSectionTitle>
        <div style={{ marginBottom: 12 }}>{renderSortToolbar()}</div>
        {renderTradeListBody()}
        {renderInfiniteScrollFooter()}
      </section>
    );
  }

  return (
    <>
      {titleOutside && (
        <DashboardSectionTitle
          title="Trades"
          count={total > 0 ? total : null}
          marginBottom={10}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
            {includeOpenPositionsControl}
            {typeof onSyncClick === 'function' && (
              <PrimaryButton
                variant="compact"
                size="sm"
                onClick={onSyncClick}
                title="Sync trade history"
                style={{ height: 28, minHeight: 28, paddingTop: 0, paddingBottom: 0, fontSize: '0.8rem' }}
              >
                Sync
              </PrimaryButton>
            )}
          </div>
        </DashboardSectionTitle>
      )}
    <Card className="mb-3 recent-trades-card" style={cardShellStyle}>
      {!titleOutside && (
      <Card.Header
        style={{
          borderBottom: `1px solid ${colors.border}`,
          backgroundColor: colors.bgSecondary,
          padding: useCompactLayout ? '10px 12px' : '12px 16px',
        }}
      >
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: useCompactLayout ? 8 : 12,
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 8,
              flexWrap: 'wrap',
              width: '100%',
            }}
          >
            <h5 className="mb-0" style={{ fontSize: '1rem', fontWeight: 600, color: colors.text }}>
              Trades
              {total > 0 && (
                <span style={{ marginLeft: '8px', fontWeight: 400, fontSize: '0.875rem', color: colors.textMuted }}>
                  ({total})
                </span>
              )}
            </h5>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginLeft: 'auto', flexShrink: 0 }}>
              {includeOpenPositionsControl}
              {typeof onSyncClick === 'function' && (
                <PrimaryButton
                  variant="compact"
                  size="sm"
                  onClick={onSyncClick}
                  title="Sync trade history"
                  style={{ height: 28, minHeight: 28, paddingTop: 0, paddingBottom: 0, fontSize: '0.8rem' }}
                >
                  Sync
                </PrimaryButton>
              )}
              {showAdminButtons && (
                <Button
                  variant="outline-light"
                  size="sm"
                  onClick={onSettingsClick}
                  style={{
                    borderColor: colors.border,
                    color: colors.text,
                    backgroundColor: 'transparent',
                    display: 'flex',
                    alignItems: 'center',
                    padding: '3px 7px',
                  }}
                  title="Trade history settings"
                >
                  <Gear size={14} />
                </Button>
              )}
            </div>
          </div>
          {renderSortToolbar()}
        </div>
      </Card.Header>
      )}
      {titleOutside && (
        <Card.Header
          style={{
            borderBottom: `1px solid ${colors.border}`,
            backgroundColor: colors.bgSecondary,
            padding: '10px 12px',
          }}
        >
          {renderSortToolbar()}
        </Card.Header>
      )}
      <Card.Body
        style={{
          padding: useCompactLayout ? '8px 12px 12px' : 0,
          backgroundColor: colors.bgSecondary,
        }}
      >
        {renderTradeListBody()}
        {renderInfiniteScrollFooter()}
      </Card.Body>
    </Card>
    </>
  );
};

/** Performance diagnosis */
// eslint-disable-next-line no-unused-vars
const PerformanceDiagnosis = ({ colors }) => {
  return (
    <div
      style={{
        background: colors.bgCard,
        border: `1px solid ${colors.border}`,
        borderRadius: '12px',
        padding: '20px',
        marginBottom: '24px',
        position: 'relative',
        overflow: 'visible',
      }}
    >
      <OverlayTrigger
        placement="left"
        overlay={
          <Tooltip id="ai-diagnosis-tooltip" style={{ maxWidth: '260px' }}>
            AI analyzes your trading data to surface patterns and insights.
          </Tooltip>
        }
      >
        <div
          style={{
            position: 'absolute',
            top: 0,
            right: 0,
            width: '52px',
            height: '52px',
            background: 'linear-gradient(135deg, #e8c547 0%, #d4af37 50%, #b8860b 100%)',
            clipPath: 'polygon(100% 0, 100% 100%, 0 0)',
            boxShadow: '0 2px 6px rgba(0,0,0,0.3)',
            cursor: 'help',
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'flex-end',
            padding: '6px 8px 0 0',
          }}
        >
          <span style={{ color: '#1a1a1a', fontSize: '0.85rem', fontWeight: 700, letterSpacing: '0.5px' }}>
            AI
          </span>
        </div>
      </OverlayTrigger>
      <div style={{ fontSize: '0.875rem', color: colors.textMuted, textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '16px', paddingRight: '56px' }}>
        Performance Diagnosis
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '24px 0 8px', gap: '8px' }}>
        <div style={{ fontSize: '1.25rem' }}>🔬</div>
        <div style={{ fontSize: '0.9rem', fontWeight: 600, color: colors.text }}>Coming Soon</div>
        <div style={{ fontSize: '0.8rem', color: colors.textMuted, textAlign: 'center', maxWidth: '320px' }}>
          AI-powered analysis of your trading patterns, strengths, and areas to improve.
        </div>
      </div>
    </div>
  );
};

const PerformanceDashboard = ({ hideHeader = false, hideDashboardHeader = false, dashboardTitle, embeddedMobile = false }) => {
  const { colors } = useTheme();
  const { user } = useContext(AuthContext);
  const {
    selectedAccount,
    isPaperTrading,
    positions: livePositions,
    setSelectedTickerDetails,
  } = useContext(TradingContext);
  const { setMarketAggregates } = useContext(StreamsContext);
  const { activeChartId, updateChartSettings } = useContext(AppStateContext);
  const { getTicker } = useGetTicker();
  const { calculateUnrealizedPL } = usePositionCalculations();
  const realtimeMetrics = useRealtimeAccountMetrics();
  const navigate = useNavigate();
  const location = useLocation();
  const isSuperuser = !!user?.superuser;
  const isMobile = useIsMobile(768, 1024);
  const onTradePage = location.pathname === '/trade';

  const [timeframe, setTimeframe] = useState(() => (
    readStoredPeriod('pt:reviewPeriod:performance', 'quarter', TIMEFRAME_PRESETS.map((preset) => preset.id))
  ));
  const [dateRange, setDateRange] = useState(null);
  const [benchmark, setBenchmark] = useState('SPY');
  const [includeOpenPositions, setIncludeOpenPositions] = useState(false);

  const handleTimeframeChange = useCallback(({ timeframe: nextTf, dateRange: nextRange }) => {
    setTimeframe(nextTf);
    setDateRange(nextRange);
    writeStoredPeriod('pt:reviewPeriod:performance', nextTf, TIMEFRAME_PRESETS.map((preset) => preset.id));
  }, []);

  const accountId =
    selectedAccount?.AccountID != null ? String(selectedAccount.AccountID) : undefined;
  // Use paper flag from the selected account immediately (isPaperTrading context lags one render).
  const paperTrading = selectedAccount?.isPaperTrading ?? isPaperTrading;
  // Don't fire any queries until the account context has settled.
  // This prevents 2-3 duplicate request cycles as TradingContext loads.
  const dataReady = !!accountId;
  const queryClient = useQueryClient();
  const { showMessage } = useContext(BannerContext);
  const [showSyncModal, setShowSyncModal] = useState(false);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [calendarDayModal, setCalendarDayModal] = useState(null);

  const { getTradeJournals } = useTradeJournals();
  const { data: journalsList } = useQuery({
    queryKey: ['tradeJournals'],
    queryFn: getTradeJournals,
    staleTime: 60000,
    enabled: dataReady,
  });
  const { journaledTradeIdSet, tradeIdToJournalMap } = useMemo(() => {
    const set = new Set();
    const map = new Map(); // trade_id → journal object
    if (Array.isArray(journalsList)) {
      for (const j of journalsList) {
        if (j.trade_id) {
          set.add(j.trade_id);
          map.set(j.trade_id, j);
        }
      }
    }
    return { journaledTradeIdSet: set, tradeIdToJournalMap: map };
  }, [journalsList]);

  const handleCalendarDayClick = useCallback((dateKey, daySummary) => {
    setCalendarDayModal({ dateKey, daySummary });
  }, []);

  const backendUrl = process.env.REACT_APP_BACKEND_URL || '';

  const handleSyncSuccess = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['performance'] });
  }, [queryClient]);

  const { activeJob: activeSyncJob, trackJob: trackSyncJob } = useTradeHistorySyncJobMonitor({
    authToken: user?.token,
    backendUrl,
    onSuccess: handleSyncSuccess,
  });

  const calendarDateRange = useMemo(
    () => getMonthGridFetchRange(timeframe, dateRange),
    [timeframe, dateRange]
  );

  const { summary, equity, calendar, distribution, isLoading, isEquityLoading } =
    usePerformanceData({
      timeframe,
      dateRange,
      calendarDateRange,
      accountId,
      account: selectedAccount,
      paperTrading,
      benchmark,
      includeOpenPositions,
      enabled: dataReady,
    });

  // Daily snapshots land at 8pm ET. Until that print, the latest curve value
  // follows the live account balance so current P&L includes today's change.
  const liveEquity = realtimeMetrics.isReady && Number(realtimeMetrics.accountValue) > 0
    ? Number(realtimeMetrics.accountValue)
    : null;
  const equityForDisplay = useMemo(
    () => applyLiveBalanceToEquityCurve(equity, liveEquity, {
      rangeUntil: dateRange?.until || null,
    }),
    [equity, liveEquity, dateRange?.until],
  );

  const { data: accountTransfers = [] } = useAccountTransfers({
    accountId,
    paperTrading,
    since: equityForDisplay?.rangeStart || null,
    until: equityForDisplay?.rangeEnd || null,
    enabled: dataReady,
  });

  const chartTimeframe = dateRange ? 'custom' : timeframe;

  const calendarDaySummaryMap = useMemo(() => {
    const map = new Map();
    for (const d of calendar?.days ?? []) {
      if (d?.date) map.set(d.date, d);
    }
    return map;
  }, [calendar?.days]);

  const todayPnl = realtimeMetrics.isReady
    ? realtimeMetrics.todayChange
    : summary?.todayPnl ?? null;

  // Mobile embeds this under /trade — refetch on mount so closed trades show without full page refresh.
  // We invalidate the whole ['performance'] prefix once when the account is ready, then rely on
  // React Query's key-based refetch for timeframe/benchmark changes (no extra effect needed there).
  useEffect(() => {
    if (!dataReady) return;
    queryClient.invalidateQueries({ queryKey: ['performance'] });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataReady, queryClient]);

  const handleTradeSymbolClick = useCallback(
    async (symbol) => {
      if (!symbol) return;
      if (canAccessTradeWorkspace(user, selectedAccount) === false) return;
      const tickerDetails = await getTicker(symbol);
      if (!tickerDetails || tickerDetails.Errors?.length || !tickerDetails.Symbols?.length) {
        return;
      }
      if (activeChartId) {
        updateChartSettings(activeChartId, { ticker: symbol });
      }
      setSelectedTickerDetails(tickerDetails.Symbols[0]);
      setMarketAggregates({ bids: [], asks: [] });

      const goToChart = () => {
        window.dispatchEvent(
          new CustomEvent('positions:tickerClicked', { detail: { ticker: symbol } })
        );
      };

      if (isMobile && !onTradePage) {
        navigate('/trade', { state: { page: 'trade' } });
        setTimeout(goToChart, 300);
      } else if (isMobile) {
        goToChart();
      } else if (!onTradePage) {
        navigate('/trade');
      }
    },
    [
      activeChartId,
      getTicker,
      isMobile,
      navigate,
      onTradePage,
      selectedAccount,
      setMarketAggregates,
      setSelectedTickerDetails,
      updateChartSettings,
      user,
    ]
  );

  const pageShellStyle = useMemo(() => {
    const base = getDashboardPageShellStyle({ isMobile });
    if (embeddedMobile && isMobile) {
      return { ...base, padding: 0 };
    }
    return base;
  }, [embeddedMobile, isMobile]);

  if (user && !isBrokerLinked(user)) {
    return (
      <>
        {!hideHeader && <Header />}
        <Container fluid style={pageShellStyle}>
          {(dashboardTitle || !hideDashboardHeader) && (
            <DashboardPageHeader
              title={dashboardTitle || 'Performance'}
              description="Track your progress and trading performance"
              hideTitle={isMobile}
            />
          )}
          <BrokerNotConnectedState
            message="Connect your broker to track your account performance, equity curve, and trade history."
            minHeight={isMobile ? 260 : 320}
          />
        </Container>
      </>
    );
  }

  // One height for every control in the toolbar row: the mobile timeframe trigger is a
  // FilterChip, so anything beside it has to match MOBILE_UI.chipHeight or it sits proud.
  const toolbarControlHeight = isMobile ? MOBILE_UI.chipHeight : 40;

  const settingsButton = (
    <Button
      variant="outline-light"
      size="sm"
      onClick={() => setShowSettingsModal(true)}
      style={{
        borderColor: colors.border,
        color: colors.text,
        backgroundColor: 'transparent',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: toolbarControlHeight,
        minHeight: toolbarControlHeight,
        width: toolbarControlHeight,
        padding: 0,
        flexShrink: 0,
      }}
      title="Trade history settings"
      aria-label="Trade history settings"
    >
      <Gear size={16} />
    </Button>
  );

  const transferAndTimeframeRow = (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        flex: isMobile ? '1 1 auto' : '0 1 auto',
        minWidth: 0,
        justifyContent: 'flex-end',
      }}
    >
      <OutlinePrimaryButton
        size="sm"
        onClick={() => setShowTransferModal(true)}
        title="View and record deposits and withdrawals"
        style={{
          height: toolbarControlHeight,
          minHeight: toolbarControlHeight,
          padding: isMobile ? '0 14px' : '0 16px',
          fontSize: isMobile ? MOBILE_UI.font.body : '0.875rem',
          flexShrink: 0,
          // Pill next to the mobile FilterChip, square-ish next to the desktop select.
          borderRadius: isMobile ? MOBILE_UI.radiusChip : 8,
        }}
        data-testid="add-transfer-button"
      >
        Transfers
      </OutlinePrimaryButton>
      {/* Content-sized on every breakpoint: stretching this wrapper pinned the timeframe
          control to the far edge and left dead space to the right of Transfers. */}
      <div style={{ flex: '0 0 auto', minWidth: 0, maxWidth: '100%' }}>
        <PerformanceTimeframeBar
          timeframe={chartTimeframe === 'custom' ? 'custom' : timeframe}
          dateRange={dateRange}
          onChange={handleTimeframeChange}
          colors={colors}
          isMobile={isMobile}
          align="right"
          emphasis
        />
      </div>
    </div>
  );

  const showPageHeader = !!(dashboardTitle || !hideDashboardHeader);
  // Desktop parks the gear beside the page title, so the action row is Transfers then timeframe.
  const gearBesideTitle = !isMobile && showPageHeader;

  const filterControls = dataReady ? (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: isMobile ? 8 : 12,
        flexWrap: 'wrap',
        justifyContent: isMobile ? 'space-between' : 'flex-end',
        width: '100%',
      }}
    >
      {isMobile || gearBesideTitle ? null : settingsButton}
      {transferAndTimeframeRow}
    </div>
  ) : null;

  const content = (
    <Container fluid style={pageShellStyle}>
      {showPageHeader ? (
        <DashboardPageHeader
          title={dashboardTitle || 'Performance'}
          description={isLoading ? 'Loading performance data…' : 'Track your progress and trading performance'}
          hideTitle={isMobile && hideDashboardHeader}
          titleAdornment={gearBesideTitle && dataReady ? settingsButton : null}
          actions={isMobile ? null : filterControls}
        />
      ) : (
        !isMobile && filterControls && (
          <div style={{ marginBottom: 24 }}>
            {filterControls}
          </div>
        )
      )}

      {/* Account Metrics header + key stats */}
      <div style={{ marginBottom: '4px' }}>
        <DashboardSectionTitle title="Account Metrics" marginBottom={10}>
          <ScopeChip label={getTimeframeScopeLabel(timeframe, dateRange)} colors={colors} />
        </DashboardSectionTitle>
        <KeyStatsRow
          summary={summary}
          equity={equityForDisplay}
          colors={colors}
          isMobile={isMobile}
          transfers={accountTransfers}
        />
      </div>

      {isMobile && dataReady && (
        <div style={{ marginBottom: 4 }}>
          <DashboardSectionTitle
            title="Account Value"
            marginBottom={10}
            style={{ alignItems: 'flex-start' }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
              {transferAndTimeframeRow}
              {settingsButton}
            </div>
          </DashboardSectionTitle>
        </div>
      )}

      {/* Equity curve */}
      {(() => {
        const drawdownAmt = summary?.currentEquity != null && summary?.allTimeHigh != null
          ? summary.currentEquity - summary.allTimeHigh : null;
        const drawdownPctVal = drawdownAmt != null && summary.allTimeHigh > 0
          ? (drawdownAmt / summary.allTimeHigh) * 100 : null;
        const isAtATH = drawdownAmt != null && drawdownAmt >= 0;
        return (
          <EquityCurveChart
            equityData={equityForDisplay}
            timeframe={chartTimeframe === 'custom' ? 'custom' : timeframe}
            dateRange={dateRange}
            onTimeframeChange={handleTimeframeChange}
            colors={colors}
            isMobile={isMobile}
            externalMobileHeader={isMobile}
            benchmark={benchmark}
            onBenchmarkChange={setBenchmark}
            isLoading={isEquityLoading}
            drawdownAmt={drawdownAmt}
            drawdownPctVal={drawdownPctVal}
            isAtATH={isAtATH}
            currentEquity={summary?.currentEquity ?? null}
            allTimeHigh={summary?.allTimeHigh ?? null}
            transfers={accountTransfers}
          />
        );
      })()}

      {/* Calendar */}
      <PerformanceDashboardCalendar
        calendarData={calendar}
        timeframe={timeframe}
        dateRange={dateRange}
        colors={colors}
        onDayClick={handleCalendarDayClick}
        titleOutside={isMobile}
      />

      {/* Distribution — only when closed trades exist in this timeframe */}
      {summary.totalTrades > 0 && (
        <TradeDistributionChart
          distribution={distribution}
          colors={colors}
          titleOutside={isMobile}
          isMobile={isMobile}
        />
      )}

      {/* Trades — all history via infinite scroll (not bound to page timeframe) */}
      <RecentPositionsTable
        accountId={accountId}
        paperTrading={paperTrading}
        livePositions={livePositions}
        calculateUnrealizedPL={calculateUnrealizedPL}
        colors={colors}
        isMobile={isMobile}
        isSuperuser={isSuperuser}
        onSymbolClick={handleTradeSymbolClick}
        journaledTradeIdSet={journaledTradeIdSet}
        tradeIdToJournalMap={tradeIdToJournalMap}
        showAdminButtons={false}
        onSyncClick={() => setShowSyncModal(true)}
        onSettingsClick={() => setShowSettingsModal(true)}
        includeOpenPositions={includeOpenPositions}
        onIncludeOpenPositionsChange={setIncludeOpenPositions}
        titleOutside={isMobile}
      />
    </Container>
  );

  return (
    <>
      {!hideHeader && <Header />}
      {content}
      {dataReady && (
        <PerformanceCalendarDayModal
          show={!!calendarDayModal}
          onHide={() => setCalendarDayModal(null)}
          dateKey={calendarDayModal?.dateKey}
          daySummary={
            calendarDayModal?.daySummary ??
            (calendarDayModal?.dateKey
              ? calendarDaySummaryMap.get(calendarDayModal.dateKey)
              : null)
          }
          accountId={accountId}
          paperTrading={paperTrading}
          colors={colors}
          onSymbolClick={handleTradeSymbolClick}
        />
      )}
      {dataReady && (
        <>
          <PerformanceSyncModal
            show={showSyncModal}
            onHide={() => setShowSyncModal(false)}
            accountId={accountId}
            isPaperTrading={paperTrading}
            brokerName={selectedAccount?.broker_name || selectedAccount?.institution_name}
            authToken={user?.token}
            backendUrl={backendUrl}
            showMessage={showMessage}
            onSuccess={handleSyncSuccess}
            trackJob={trackSyncJob}
            activeSyncJob={activeSyncJob}
          />
          <PerformanceTradeSyncModal
            show={showSettingsModal}
            onHide={() => setShowSettingsModal(false)}
            accountId={accountId}
            isPaperTrading={paperTrading}
            authToken={user?.token}
            backendUrl={backendUrl}
            showMessage={showMessage}
            onSuccess={handleSyncSuccess}
          />
          <AccountTransferModal
            show={showTransferModal}
            onHide={() => setShowTransferModal(false)}
            accountId={accountId}
            isPaperTrading={paperTrading}
            showMessage={showMessage}
            onSuccess={handleSyncSuccess}
          />
        </>
      )}
    </>
  );
};

export default PerformanceDashboard;
